import { isChrome } from 'src/utils/browserUtils';

import { chromeExtConfig, enviromentConfig } from 'src/config';

const CHROME_EXTENSION_ID = chromeExtConfig.id;
const DEV_CHROME_EXTENSION_ID = 'npnmkokdgghbjdbcfbanffooaknnnlaa';
const EXTENSION_PROBE_MESSAGE = 'version';

let resolvedChromeExtensionId: string | undefined;

export enum ExtensionMessage {
  VERSION = 'version',
  CLOSE_SIDEBAR = 'close_sidebar',
  GET_CAPABILITIES = 'get_capabilities',
  GET_PANEL_STATE = 'get_panel_state',
  RESET_PANEL_WIDTH = 'reset_panel_width',
  SET_PANEL_MODE = 'set_panel_mode',
  TOGGLE_PANEL_SIDE = 'toggle_panel_side',
}

/**
 * Development Chrome extension builds intentionally "just work" with the local app.
 *
 * Production and staging should only target the configured extension ID. In
 * development, though, an engineer might have either the configured extension
 * installed or the unpacked dev build installed. The dev build gets a stable ID
 * from `browser-extension/scripts/manifestTransform.cjs`, so we can probe both
 * IDs instead of requiring every engineer to update local config before testing
 * extension-to-app messaging.
 *
 * Keep the configured ID first: if someone has both extensions installed, the
 * app talks to the normal configured extension and only falls back to the dev
 * extension when that one is unavailable.
 */
function getCandidateExtensionIds(): string[] {
  if (enviromentConfig.environment !== 'development') {
    return [CHROME_EXTENSION_ID];
  }

  if (CHROME_EXTENSION_ID === DEV_CHROME_EXTENSION_ID) {
    return [CHROME_EXTENSION_ID];
  }

  return [CHROME_EXTENSION_ID, DEV_CHROME_EXTENSION_ID];
}

function probeExtension(extensionId: string): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      chrome.runtime.sendMessage(extensionId, { message: EXTENSION_PROBE_MESSAGE }, (reply) => {
        resolve(Boolean(reply));
      });
    } catch {
      resolve(false);
    }
  });
}

async function resolveChromeExtensionId(): Promise<string | undefined> {
  if (resolvedChromeExtensionId) {
    return resolvedChromeExtensionId;
  }

  const candidateExtensionIds = getCandidateExtensionIds();
  for (const extensionId of candidateExtensionIds) {
    // eslint-disable-next-line no-await-in-loop -- Probe in priority order so prod wins over dev.
    const isAvailable = await probeExtension(extensionId);
    if (isAvailable) {
      resolvedChromeExtensionId = extensionId;
      return extensionId;
    }
  }

  return undefined;
}

async function sendResolvedExtensionMessage(
  message: ExtensionMessage,
  payload: Record<string, unknown>,
  callback?: (reply: unknown) => void
) {
  const extensionId = await resolveChromeExtensionId();
  if (!extensionId) {
    console.log('No ModernLoop Chrome extension responded');
    return;
  }

  if (callback) {
    chrome.runtime.sendMessage(extensionId, { message, ...payload }, callback);
  } else {
    chrome.runtime.sendMessage(extensionId, { message, ...payload });
  }
  console.log('Sent extension message:', message);
}

export default function sendExtensionMessage(
  message: ExtensionMessage,
  payload: Record<string, unknown> = {},
  callback?: (reply: unknown) => void
) {
  if (!isChrome() || !chrome?.runtime?.sendMessage) {
    console.log('no chrome or no sendMessage');
    return;
  }

  sendResolvedExtensionMessage(message, payload, callback).catch((error) => {
    console.error(error);
  });
}
